import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router';
import { useMe } from './api/hooks.js';
import { AppShell } from './components/AppShell.js';
import { ErrorView, Loading } from './components/Page.js';
import { RewardsProvider } from './lib/rewards.js';
import { AcademyPage } from './pages/AcademyPage.js';
import { ArenaPage } from './pages/ArenaPage.js';
import { AuthPage } from './pages/AuthPage.js';
import { FramePage } from './pages/FramePage.js';
import { HangarPage } from './pages/HangarPage.js';
import { ProfilePage } from './pages/ProfilePage.js';

// Pages that embed the code editor are split out so the shell loads without Monaco.
const LessonPage = lazy(() => import('./pages/LessonPage.js').then((m) => ({ default: m.LessonPage })));
const WorkshopPage = lazy(() => import('./pages/WorkshopPage.js').then((m) => ({ default: m.WorkshopPage })));
const BattlePage = lazy(() => import('./pages/BattlePage.js').then((m) => ({ default: m.BattlePage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage.js').then((m) => ({ default: m.LibraryPage })));

const withEditor = (node: ReactNode) => <Suspense fallback={<Loading />}>{node}</Suspense>;

function RequireAuth() {
  const { data: me, isLoading, error } = useMe();
  if (isLoading) return <Loading />;
  if (error) return <ErrorView error={error} />;
  if (!me) return <Navigate to="/login" replace />;
  return (
    <RewardsProvider>
      <Outlet />
    </RewardsProvider>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <AuthPage mode="login" /> },
  { path: '/register', element: <AuthPage mode="register" /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <HangarPage /> },
          { path: '/academy', element: <AcademyPage /> },
          { path: '/academy/:lessonId', element: withEditor(<LessonPage />) },
          { path: '/workshop', element: withEditor(<WorkshopPage />) },
          { path: '/frame', element: <FramePage /> },
          { path: '/library', element: withEditor(<LibraryPage />) },
          { path: '/arena', element: <ArenaPage /> },
          { path: '/arena/battles/:battleId', element: withEditor(<BattlePage />) },
          { path: '/profile', element: <ProfilePage /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
